import { LexicalEditor, $getSelection, $isRangeSelection, $isTextNode, $getNodeByKey, $createTextNode } from 'lexical'
import { $createDirectiveNode } from '@mdxeditor/editor'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import DisposableReactionsStore from 'reusable/mobx-store-utils/DisposableReactionsStore'
import MarkdownStore from '../../MarkdownStore'
import { PopoverPosition } from '../../popover/PopoverSurface'
import { MentionSelection, MentionUser } from '../../popover/MentionPopover'
import { makeAutoObservable } from 'mobx'

interface OpenState {
   anchor: PopoverPosition
   query: string
   nodeKey: string
   /** Offset of `@` within the text node. */
   triggerOffset: number
}

interface MentionStoreProps {
   editor: LexicalEditor
   markdownStore: MarkdownStore
}

function getCaretRect(): PopoverPosition | null {
   const windowSelection = window.getSelection()
   if (!windowSelection || windowSelection.rangeCount === 0) return null
   const caretRect = windowSelection.getRangeAt(0).getBoundingClientRect()
   if (!caretRect) return null
   if (caretRect.width === 0 && caretRect.height === 0 && caretRect.top === 0 && caretRect.left === 0) return null
   return { left: caretRect.left, top: caretRect.top, bottom: caretRect.bottom }
}

export default class MentionStore {
   sp
   open: OpenState | null = null
   datePicker: PopoverPosition | null = null
   /** Fingerprint of the last `@` we processed — so once the user dismisses, we
    *  don't immediately re-open the same trigger. Cleared on text mutation. */
   private consumedTrigger = ''

   constructor(sp: MentionStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get team(): MentionUser[] {
      return (this.sp.markdownStore.sp.users ?? []) as MentionUser[]
   }

   get clientUsers(): MentionUser[] {
      return (this.sp.markdownStore.sp.clientUsers ?? []) as MentionUser[]
   }

   get clientOrgName() {
      return this.sp.markdownStore.sp.clientOrgName
   }

   /** Subscribe to Lexical updates so the popover opens / tracks query / dismisses
    *  in lockstep with the editor's committed state. */
   configureReactions = (reactionsStore: DisposableReactionsStore) => {
      reactionsStore.registerDisposer(
         this.sp.editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => this.handleEditorUpdate())
         })
      )
   }

   close = () => {
      this.open = null
      this.datePicker = null
   }

   handleSelect = (selection: MentionSelection) => {
      if (!this.open) return
      if (selection.kind === 'user') {
         this.replaceTriggerWithDirective(this.open, { userId: selection.userId, name: selection.name, color: selection.colorKey }, 'GapAssignee')
         this.close()
      } else if (selection.kind === 'date') {
         this.replaceTriggerWithDirective(this.open, { date: selection.iso }, 'GapDate')
         this.close()
      } else {
         this.datePicker = this.open.anchor
      }
   }

   handleDatePicked = (iso: string) => {
      if (!this.open) { this.datePicker = null; return }
      this.replaceTriggerWithDirective(this.open, { date: iso }, 'GapDate')
      this.close()
   }

   private handleEditorUpdate = () => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
         if (this.open) this.open = null
         return
      }
      const textNode = selection.anchor.getNode()
      if (!$isTextNode(textNode)) {
         if (this.open) this.open = null
         return
      }

      const text = textNode.getTextContent()
      const cursorOffset = selection.anchor.offset
      const nodeKey = textNode.getKey()

      // If the popover is already open, track query / dismissal.
      if (this.open) {
         const openState = this.open
         if (openState.nodeKey !== nodeKey) { this.open = null; return }
         if (text[openState.triggerOffset] !== '@') { this.open = null; return }
         if (cursorOffset <= openState.triggerOffset) { this.open = null; return }
         const query = text.slice(openState.triggerOffset + 1, cursorOffset)
         if (/\s/.test(query)) { this.open = null; return }
         if (query !== openState.query) this.open = { ...openState, query }
         return
      }

      // Closed: open if caret sits right after a `@` we haven't consumed yet.
      if (cursorOffset < 1 || text[cursorOffset - 1] !== '@') return
      const charBeforeTrigger = cursorOffset >= 2 ? text[cursorOffset - 2] : ''
      const isValidTriggerStart = cursorOffset === 1 || /\s/.test(charBeforeTrigger)
      if (!isValidTriggerStart) return

      const fingerprint = `${nodeKey}:${cursorOffset}:${text.length}`
      if (this.consumedTrigger === fingerprint) return

      // Defer rect measurement to the next frame so layout has flushed.
      requestAnimationFrame(() => {
         const anchor = getCaretRect()
         if (!anchor) return
         this.consumedTrigger = fingerprint
         this.open = { anchor, query: '', nodeKey, triggerOffset: cursorOffset - 1 }
      })
   }

   /** Atomic: replace the `@query` range with the directive node, keeping it inside
    *  the same paragraph as the trigger. Going through `insertDirective$` would call
    *  `$insertNodes` after our delete leaves the host empty, MDXEditor then notices
    *  the inline node landed at the root, and wraps it in a new paragraph — which is
    *  why the caret used to drop onto a new line. */
   private replaceTriggerWithDirective = (openState: OpenState, attributes: Record<string, string>, directiveName: string) => {
      this.sp.editor.update(() => {
         const triggerNode = $getNodeByKey(openState.nodeKey)
         if (!triggerNode || !$isTextNode(triggerNode)) return
         const focusOffset = Math.min(openState.triggerOffset + 1 + openState.query.length, triggerNode.getTextContent().length)
         const triggerRange = triggerNode.select(openState.triggerOffset, focusOffset)
         const directive = $createDirectiveNode({ type: 'textDirective', name: directiveName, attributes, children: [] })
         // Trailing single space gives the caret a real position that survives Lexical
         // normalisation; the user can backspace it if undesired.
         const trailingSpace = $createTextNode(' ')
         triggerRange.insertNodes([directive, trailingSpace])
         trailingSpace.select(0, 0)
      })
   }
}

export const { Provider: MentionStoreProvider, useProvidedStore: useMentionStore } = createStoreContext(MentionStore)
